import MonitoringLocalStorageManager, { BuiltInLocalStorage } from '../../util/storage/MonitoringLocalStorageManager'
import builtInSelectors from './selectors'
import { builtInOrigin } from './model'
import { Dispatch, ExtraArgs, GetState } from '../../types'
import { API, Environment } from '../../remote-api/models'
import { fetchMainSelectorsOptionsAndSelect, selectEnvironmentAndFetchAllResources } from '../../common/selector/actions'
import { fetchDetailedResource, storeResourcesAndSelectOne } from '../../common/resource/actions'
import { storeEnvironmentsAndSelectOne } from '../../common/environment/actions'
import { storeVersionsAndSelectOne } from '../../common/version/actions'
import { getDashboardType } from './dashboardType'
import { selectors as authSelectors } from '../../lifecycle/auth'
import { builtInDetailUrl, goToBuiltIn, goToLogPoints } from '../Sidebar/Urls'
import { TimeRange } from '@mulesoft/monitoring-center-components/lib/components/BuiltInDashboards/PreBuilt'
import { AutoRefreshFrequency } from '@mulesoft/monitoring-center-components/lib/components/Toolbar/AutoRefresh/model'
import { ResourceType as ComponentsResourceType } from '@mulesoft/monitoring-center-components/lib/models/resource'
import { EnvironmentSelectorState } from '../../common/environment/reducer'
import { ResourceSelectorState } from '../../common/resource/reducer'
import { VersionSelectorState } from '../../common/version/reducer'
import { fetchFeaturesCompatibility } from '../../common/compatibility/actions'
import { LogPointsService } from '../LogPoints/util/LogPointsService'
import { IframeLoadingState } from '@mulesoft/monitoring-center-components'

import {
  DetailedResource,
  getComponentsResourceTypeFromResource,
  getResourceByInfluxResourceId,
  getResourceId,
  getResourceMuleVersion,
  getResourceType,
  isApplicationResource,
  isAsset,
  isManagedAPI,
  isTarget,
  Resource,
  ResourceType
} from '../../common/resource/model'
import { LoadProps, OnViewProps } from './BuiltInDashboard'
import { isPCEOrGov } from '../../util/environments'
import { EmptyStatePageStateOverride } from '../EmptyStatePage/model'
import { changeEmptyState } from '../EmptyStatePage/actions'
import { ResourceNotFoundError } from '../../common/error/ResourceNotFoundError'
import { isFlex } from '../../util/flex';

export enum ActionType {
  SetBuiltInStateFromLandingPage = 'Monitoring/BuiltIn/SetBuiltInStateFromLandingPage',
  ChangeBuiltInState = 'Monitoring/Iframe/ChangeBuiltInState',
  ChangeBuiltInHiddenDetail = 'Monitoring/BuiltIn/Detail/ChangeHiddenDetail',
  ViewLogs = 'Monitoring/BuiltIn/ViewLogs',
  ManageAPI = 'Monitoring/BuiltIn/ManageAPI',
  ManageApplication = 'Monitoring/BuiltIn/ManageApplication',
  ManageTarget = 'Monitoring/BuiltIn/ManageTarget',
  ConfigureLogPoints = 'Monitoring/BuiltIn/ConfigureLogPoints',
  SaveAs = 'Monitoring/BuiltIn/SaveAs',
  SavedAsCustomDashboard = 'Monitoring/BuiltIn/SavedAsCustomDashboard',
  FetchDetailedResource = 'Monitoring/BuiltIn/FetchDetailedResource',
  FetchedDetailedResource = 'Monitoring/BuiltIn/FetchedDetailedResource',
  IsLogPointsCompatible = 'Monitoring/BuiltIn/IsLogPointsCompatible',
  RangeChange = 'Monitoring/BuiltIn/RangeChange',
  AutoRefreshChange = 'Monitoring/BuiltIn/AutoRefreshChange',
  ViewedAPIAnalytics = 'Monitoring/BuiltIn/ViewedAPIAnalytics',
}

// Basic Actions

export type SetBuiltInStateFromLandingPageAction = {
  type: ActionType.SetBuiltInStateFromLandingPage
  payload: {
    environmentSelector: EnvironmentSelectorState,
    resourceSelector: ResourceSelectorState,
    versionSelector: VersionSelectorState
  }
}

export type ChangeBuiltInStateAction = {
  type: ActionType.ChangeBuiltInState
  payload: {
    isLoading: IframeLoadingState,
  }
}

export type ChangeBuiltInHiddenDetailAction = {
  type: ActionType.ChangeBuiltInHiddenDetail
  payload: {
    isDetailHidden: boolean
  }
}

export type ViewLogsAction = {
  type: ActionType.ViewLogs
}

export type ManageAPIAction = {
  type: ActionType.ManageAPI
}

export type ManageApplicationAction = {
  type: ActionType.ManageApplication
}

export type ManageTargetAction = {
  type: ActionType.ManageTarget
}

export type ConfigureLogPointsAction = {
  type: ActionType.ConfigureLogPoints
}

export type SaveAsAction = {
  type: ActionType.SaveAs
  payload: { tabName: string }
}

export type SavedAsCustomDashboardAction = {
  type: ActionType.SavedAsCustomDashboard
  payload: { tabName: string }
}

export type FetchDetailedResourceAction = {
  type: ActionType.FetchDetailedResource
}

export type FetchedDetailedResourceAction = {
  type: ActionType.FetchedDetailedResource
  payload: { detailedResource?: DetailedResource }
}

export type RangeChangeAction = {
  type: ActionType.RangeChange
  payload: { range: TimeRange }
}

export type AutoRefreshChangeAction = {
  type: ActionType.AutoRefreshChange
  payload: { checked: boolean, frequency: AutoRefreshFrequency }
}

export type ViewedAPIAnalyticsAction = {
  type: ActionType.ViewedAPIAnalytics
  payload: { assetId: string, versionId: string }
}

export type IsLogPointsCompatibleAction = {
  type: ActionType.IsLogPointsCompatible
  payload: { compatible: boolean }
}

export type BuiltInAction =
  | SetBuiltInStateFromLandingPageAction
  | ChangeBuiltInStateAction
  | ChangeBuiltInHiddenDetailAction
  | ViewLogsAction
  | ManageAPIAction
  | ManageApplicationAction
  | ManageTargetAction
  | ConfigureLogPointsAction
  | SaveAsAction
  | SavedAsCustomDashboardAction
  | FetchDetailedResourceAction
  | FetchedDetailedResourceAction
  | IsLogPointsCompatibleAction
  | RangeChangeAction
  | AutoRefreshChangeAction
  | ViewedAPIAnalyticsAction

// Basic Actions Creators

export const setBuiltInStateFromLandingPageAction = (environmentSelector: EnvironmentSelectorState,
                                                     resourceSelector: ResourceSelectorState,
                                                     versionSelector: VersionSelectorState): BuiltInAction => ({
  type: ActionType.SetBuiltInStateFromLandingPage,
  payload: {
    environmentSelector: environmentSelector,
    resourceSelector: resourceSelector,
    versionSelector: versionSelector
  }
})

export const changeBuiltInStateAction = (isLoading: IframeLoadingState): BuiltInAction => ({
  type: ActionType.ChangeBuiltInState,
  payload: {isLoading: isLoading}
})

const changeBuiltInStateHiddenDetailAction = (isDetailHidden: boolean): BuiltInAction => ({
  type: ActionType.ChangeBuiltInHiddenDetail,
  payload: {isDetailHidden: isDetailHidden}
})

const viewLogsAction = (): BuiltInAction => ({
  type: ActionType.ViewLogs
})

const manageAPIAction = (): BuiltInAction => ({
  type: ActionType.ManageAPI,
})

const manageApplicationAction = (): BuiltInAction => ({
  type: ActionType.ManageApplication,
})

const manageTargetAction = (): BuiltInAction => ({
  type: ActionType.ManageTarget,
})

const configureLogPointsAction = (): BuiltInAction => ({
  type: ActionType.ConfigureLogPoints
})

const saveAsAction = (tabName: string): BuiltInAction => ({
  type: ActionType.SaveAs,
  payload: {tabName}
})

const savedAsCustomDashboardAction = (tabName: string): BuiltInAction => ({
  type: ActionType.SavedAsCustomDashboard,
  payload: {tabName}
})

const fetchDetailedResourceAction = (): BuiltInAction => ({
  type: ActionType.FetchDetailedResource
})

const fetchedDetailedResourceAction = (detailedResource?: DetailedResource): BuiltInAction => ({
  type: ActionType.FetchedDetailedResource,
  payload: {detailedResource}
})

const isLogPointsCompatibleAction = (compatible: boolean): BuiltInAction => ({
  type: ActionType.IsLogPointsCompatible,
  payload: {compatible}
})

const rangeChangeAction = (range: TimeRange): BuiltInAction => ({
  type: ActionType.RangeChange,
  payload: {range}
})

const autoRefreshChangeAction = (checked: boolean, frequency: AutoRefreshFrequency): BuiltInAction => ({
  type: ActionType.AutoRefreshChange,
  payload: {checked, frequency}
})

export const viewedAPIAnalyticsAction = (assetId: string, versionId: string): BuiltInAction => ({
  type: ActionType.ViewedAPIAnalytics,
  payload: {assetId, versionId}
})

// Thunks

export const rangeChange = (range: TimeRange) => (dispatch: Dispatch) => {
  dispatch(rangeChangeAction(range))

  const lsBuiltIn = MonitoringLocalStorageManager.get(builtInOrigin) as BuiltInLocalStorage

  if (lsBuiltIn) {
    MonitoringLocalStorageManager.put('builtIn', {...lsBuiltIn, range: range})
  }
}

export const autoRefreshChange = (checked: boolean, frequency: AutoRefreshFrequency) => (dispatch: Dispatch) => {
  dispatch(autoRefreshChangeAction(checked, frequency))
  const lsBuiltIn = MonitoringLocalStorageManager.get(builtInOrigin) as BuiltInLocalStorage
  if (lsBuiltIn) {
    MonitoringLocalStorageManager.put('builtIn', {
      ...lsBuiltIn, autoRefresh: {
        checked: checked,
        frequency: checked ? frequency : null
      }
    })
  }
}

export const dispatchViewedBuiltIn = (resource: Resource, versionId?: string) => (dispatch: Dispatch) => {
  if (isAsset(resource) && versionId) {
    dispatch(viewedAPIAnalyticsAction(resource.assetId, versionId))
  }
}

export const viewBuiltInDashboards = (props: OnViewProps) => (dispatch: Dispatch) => {
  const {environmentSelector, envId, targetId} = props

  if (environmentSelector.selected && environmentSelector.selected.id === envId) {
    dispatch(onBuiltInDashboardsView(props))
    dispatch(loadBuiltInDashboards(props))
  } else {
    const options = {origin: builtInOrigin, withMetrics: true, targetId}
    dispatch(selectEnvironmentAndFetchAllResources(options, envId, environmentSelector.resources)).then(() => {
      dispatch(onBuiltInDashboardsView(props))
      dispatch(loadBuiltInDashboards(props))
    })
  }
}

export const onBuiltInDashboardsView = (props: OnViewProps) => (dispatch: Dispatch, getState: GetState) => {
  const {dashboardType, envId, resourceId, componentsResourceType, versionId, hasDashboardTypeChanged} = props

  const resourceSelector = builtInSelectors.resourceSelector.getAll(getState())
  const resource = resourceSelector.resources && resourceSelector.resources.find((resource: Resource) => {
    if (componentsResourceType === ComponentsResourceType.RTF_APPLICATION) {
      let resourceType = getComponentsResourceTypeFromResource(resource)
      let isRTForCloudHub2 = resourceType === ComponentsResourceType.RTF_APPLICATION || resourceType === ComponentsResourceType.CLOUDHUB2_APPLICATION
      return getResourceId(resource) === resourceId && isRTForCloudHub2
    }
    return getResourceId(resource) === resourceId && getComponentsResourceTypeFromResource(resource) === componentsResourceType
  })

  const lsBuiltIn = MonitoringLocalStorageManager.get(builtInOrigin) as BuiltInLocalStorage
  const isTitanium = authSelectors.isMonitoringCenterPremium(getState())

  if (lsBuiltIn && resource) {
    const actualResourceType = getResourceType(resource)
    const muleVersion = getResourceMuleVersion(resource)

    const orgId = getState().monitoring.auth.profile.organizationId
    // Only check API HTTP Metrics compatibility for APIs and if it's not PCE, GovCloud or Flex
    if (!hasDashboardTypeChanged && versionId && !isPCEOrGov() && !isFlex(resource, versionId) && !isManagedAPI(resource) && !isTarget(resource)) {
      dispatch(fetchFeaturesCompatibility(orgId, envId, 'api', `${resourceId}:${versionId}`, 'monitoring')).then(() => {
        dispatch(goToBuiltInDashboards(lsBuiltIn, resource, isTitanium, dashboardType, envId, actualResourceType, resourceId, versionId, muleVersion))
      }).catch(() => {
        dispatch(goToBuiltInDashboards(lsBuiltIn, resource, isTitanium, dashboardType, envId, actualResourceType, resourceId, versionId, muleVersion))
      })
    } else {
      dispatch(goToBuiltInDashboards(lsBuiltIn, resource, isTitanium, dashboardType, envId, actualResourceType, resourceId, versionId, muleVersion))
    }
  }
}

const goToBuiltInDashboards = (lsBuiltIn: BuiltInLocalStorage, resource: Resource, isTitanium: boolean, dashboardType: string, envId: string,
                               resourceType: ResourceType, resourceId: string, versionId?: string, muleVersion?: string) =>
  (dispatch: Dispatch, getState: GetState) => {
    const featuresCompatibility = getState().monitoring.featuresCompatibility.resources
    const apiMetricsCompatible = featuresCompatibility ? featuresCompatibility.apiMetrics.compatible : false
    const isPCEOrGovOrFlex = isPCEOrGov() || isFlex(resource, versionId);
    const resolvedDashboardType =
      getDashboardType(isTitanium, resourceType, dashboardType, muleVersion, isPCEOrGovOrFlex ? true : apiMetricsCompatible)

    const value = {
      ...lsBuiltIn,
      selectedEnvironment: envId,
      selectedResource: resourceId,
      selectedVersion: versionId || null,
      dashboardType: resolvedDashboardType
    }

    MonitoringLocalStorageManager.put(builtInOrigin, value)

    goToBuiltIn(envId, resolvedDashboardType, resource, versionId)
  }

const fetchEnvironmentFromServerAndSelect = (dispatch: Dispatch,
                                             getState: GetState,
                                             fetchMainSelectorsOptionsAndSelectAction: (dispatch: Dispatch) => Promise<void>,
                                             fetchDetailedResourceAndLogPointsUsingResource: (resource: Resource) => void) => {
  const redirectTo404Page = (emptyState: EmptyStatePageStateOverride) => dispatch(changeEmptyState(404, emptyState))

  dispatch(fetchMainSelectorsOptionsAndSelectAction)
    .then(() => {
      const newResource = getState().monitoring.builtIn.resourceSelector.selected as Resource
      fetchDetailedResourceAndLogPointsUsingResource(newResource)
    })
    .catch((err) => {
      if (err instanceof ResourceNotFoundError) {
        const emptyStatePageStateOverride: EmptyStatePageStateOverride = {
          redirectLink: {
            label: 'Back to Built-in Dashboards',
            href: '#/builtin'
          },
        }
        redirectTo404Page(emptyStatePageStateOverride)
      }
    })
}

const loadSelectors = (environmentSelector: EnvironmentSelectorState,
                       resourceSelector: ResourceSelectorState,
                       versionSelector: VersionSelectorState,
                       loadProps: LoadProps & { componentsResourceType: ComponentsResourceType },
                       environment?: Environment,
                       resource?: Resource,
                       version?: API | '') => (dispatch: Dispatch, getState: GetState, {api}: ExtraArgs) => {
  const options = {origin: builtInOrigin, withMetrics: true, targetId: loadProps.targetId, resourceType: loadProps.componentsResourceType}
  const orgId = getState().monitoring.auth.profile.organizationId

  const logPointsCompatibleValidator = (detailedResource: DetailedResource) => {
    const isLogPointsCompatibleDispatcher = (compatibility: boolean) => {
      dispatch(isLogPointsCompatibleAction(compatibility))
      return detailedResource
    }

    const remoteAPIManager = api.apiManager(getState)
    const remoteARM = api.arm(getState)

    return new LogPointsService(remoteAPIManager, remoteARM)
      .validateDetailedResourceCompatibility(orgId, detailedResource)
      .then(isLogPointsCompatibleDispatcher)
      .catch(() => detailedResource)
  }

  const fetchedDetailsDispatcher = (detailedResource: DetailedResource) => {
    dispatch(fetchedDetailedResourceAction(detailedResource))
    return detailedResource
  }

  const fetchDetailedResourceAndLogPoints = (envId: string, targetResource: Resource, versionId: string | undefined) => {
    dispatch(fetchDetailedResource(envId, targetResource, versionId))
      .then(fetchedDetailsDispatcher)
      .then(logPointsCompatibleValidator)
  }

  if (environment && environmentSelector.resources) {
    dispatch(storeEnvironmentsAndSelectOne(environment, environmentSelector.resources, builtInOrigin))
  } else {
    const fetchMainSelectorsOptionsAndSelectAction =
      fetchMainSelectorsOptionsAndSelect(options, loadProps.envId, loadProps.resourceId, loadProps.versionId)
    const fetchDetailedResourceAndLogPointsUsingResource = (newResource: Resource) => {
      if (newResource && !resource) {
        fetchDetailedResourceAndLogPoints(loadProps.envId, newResource, loadProps.versionId)
      }
    }
    fetchEnvironmentFromServerAndSelect(
      dispatch, getState,
      fetchMainSelectorsOptionsAndSelectAction,
      fetchDetailedResourceAndLogPointsUsingResource)
  }

  if (resource && resourceSelector.resources) dispatch(storeResourcesAndSelectOne(resource, resourceSelector.resources, builtInOrigin))
  if (version && versionSelector.resources) dispatch(storeVersionsAndSelectOne(version, versionSelector.resources, builtInOrigin))

  if (!loadProps.hasDashboardTypeChanged) dispatch(fetchDetailedResourceAction())
  if (resource) {
    fetchDetailedResourceAndLogPoints(loadProps.envId, resource, loadProps.versionId)
  }
}

export const loadBuiltInDashboards = (props: LoadProps & { componentsResourceType: ComponentsResourceType }) =>
  (dispatch: Dispatch, getState: GetState) => {
    const orgId = getState().monitoring.auth.profile.organizationId
    const {environmentSelector, versionSelector} = getState().monitoring.builtIn
    const resourceSelector = builtInSelectors.resourceSelector.getAll(getState())
    const environment = environmentSelector.resources && environmentSelector.resources.find(env => env.id === props.envId)
    const newResource = resourceSelector.resources &&
      getResourceByInfluxResourceId(resourceSelector.resources, props.resourceId, props.componentsResourceType, props.targetId)
    const version = props.versionId && versionSelector.resources && versionSelector.resources.find(version => version.id === props.versionId)
    const currentResource = getState().monitoring.builtIn.details.detailedResource

    if (currentResource && isApplicationResource(currentResource) && newResource && isAsset(newResource)) {
      // setting undefined to show loading spinner as then it will fetch the new resource details
      dispatch(fetchedDetailedResourceAction(undefined))
    }

    const notPCEOrGovOrFlex = !isPCEOrGov() && !isFlex(newResource, props.versionId);
    if (props.versionId && !props.hasDashboardTypeChanged && newResource && notPCEOrGovOrFlex && !isManagedAPI(newResource)) {
      dispatch(fetchFeaturesCompatibility(orgId, props.envId, 'api', `${props.resourceId}:${props.versionId}`, 'monitoring')).then(() => {
        dispatch(loadSelectors(environmentSelector, resourceSelector, versionSelector, props, environment, newResource, version))
      })
        .catch(() => {
          dispatch(loadSelectors(environmentSelector, resourceSelector, versionSelector, props, environment, newResource, version))
        })
    } else {
      dispatch(loadSelectors(environmentSelector, resourceSelector, versionSelector, props, environment, newResource, version))
    }
  }

export const mainSelectorOpen = (
  environment?: { selected: Environment, list: Environment[] },
  resource?: { selected: Resource, list: Resource[] },
  version?: { selected: API, list: API[] }
) => (dispatch: Dispatch) => {
  if (environment) dispatch(storeEnvironmentsAndSelectOne(environment.selected, environment.list, builtInOrigin))
  if (resource) dispatch(storeResourcesAndSelectOne(resource.selected, resource.list, builtInOrigin))
  if (version) dispatch(storeVersionsAndSelectOne(version.selected, version.list, builtInOrigin))
}

export const changeBuiltInHiddenDetail = (isDetailHidden: boolean) => (dispatch: Dispatch) => {
  dispatch(changeBuiltInStateHiddenDetailAction(isDetailHidden))
  const builtInLS = MonitoringLocalStorageManager.get(builtInOrigin)

  if (builtInLS) {
    MonitoringLocalStorageManager.put(builtInOrigin, {...builtInLS, isDetailHidden: isDetailHidden})
  }
}

export const saveAs = (dashboardType: string) => (dispatch: Dispatch) => {
  dispatch(saveAsAction(dashboardType))
}

export const savedAsCustomDashboard = (dashboardType: string) => (dispatch: Dispatch) => {
  dispatch(savedAsCustomDashboardAction(dashboardType))
}

export const viewLogs = (logsSearchLink: string) => (dispatch: Dispatch) => {
  dispatch(viewLogsAction())
  window.location.hash = logsSearchLink
}

export const manageAPI = (orgDomain: string,
                          organizationId: string,
                          environmentId: string,
                          resource?: DetailedResource) => (dispatch: Dispatch) => {
  window.location.href = builtInDetailUrl(resource, orgDomain, organizationId, environmentId)
  dispatch(manageAPIAction())
}

export const manageApplication = (orgDomain: string,
                                  organizationId: string,
                                  environmentId: string,
                                  resource?: DetailedResource) => (dispatch: Dispatch) => {
  window.location.href = builtInDetailUrl(resource, orgDomain, organizationId, environmentId)
  dispatch(manageApplicationAction())
}

export const manageTarget = (orgDomain: string, organizationId: string, environmentId: string,
                             resource?: DetailedResource) => (dispatch: Dispatch) => {
  window.location.href = builtInDetailUrl(resource, orgDomain, organizationId, environmentId)
  dispatch(manageTargetAction())
}

export const configureLogPoints = (environmentId: string, resource: Resource, versionId?: string) => (dispatch: Dispatch) => {
  goToLogPoints(environmentId, resource, versionId)
  dispatch(configureLogPointsAction())
}

export const privateFunctionsForTest = {
  fetchEnvironmentFromServerAndSelect
}
